import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Music4, Film, Clapperboard, Sparkles, Lock } from "lucide-react";
import api, { publicMediaUrl } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function SharePage() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [needPw, setNeedPw] = useState(false);
  const [pw, setPw] = useState("");
  const [checking, setChecking] = useState(false);

  const fetchData = async (password) => {
    try {
      const q = password ? `?pw=${encodeURIComponent(password)}` : "";
      const r = await api.get(`/public/projects/${shareId}${q}`);
      if (r.data.password_required) { setNeedPw(true); setData(null); }
      else { setData(r.data); setNeedPw(false); }
    } catch { setErr(true); }
  };

  useEffect(() => { fetchData(); }, [shareId]);

  const submitPw = async () => {
    setChecking(true);
    await fetchData(pw);
    setChecking(false);
  };

  const TYPE = { song: Music4, video: Film, shortfilm: Clapperboard };

  return (
    <div className="min-h-screen grain relative">
      <div className="pointer-events-none absolute -top-40 -right-40 w-[420px] h-[420px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(255,51,75,0.25), transparent 70%)" }} />
      <header className="relative z-10 flex items-center justify-between px-5 sm:px-10 py-6">
        <Link to="/"><Logo /></Link>
        <Link to="/" data-testid="share-cta"
          className="inline-flex items-center gap-2 bg-[#FF334B] hover:bg-[#E0223A] text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors">
          <Sparkles className="w-4 h-4" /> Crea el tuyo
        </Link>
      </header>

      <main className="relative z-10 max-w-2xl mx-auto px-5 py-10">
        {err ? (
          <div className="mfp-card p-12 text-center" data-testid="share-unavailable">
            <p className="font-head text-xl">Este enlace no está disponible.</p>
            <p className="text-sm text-[#64748B] mt-2">Puede haber caducado o el creador lo revocó.</p>
          </div>
        ) : needPw ? (
          <div className="mfp-card p-10 text-center max-w-sm mx-auto" data-testid="share-password-gate">
            <Lock className="w-10 h-10 mx-auto text-[#FF9F2E] mb-3" />
            <p className="font-head text-lg mb-1">Enlace protegido</p>
            <p className="text-sm text-[#64748B] mb-4">Introduce la contraseña para verlo.</p>
            <input data-testid="share-pw-field" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitPw()} placeholder="Contraseña"
              className="w-full bg-[#0B0C13] border border-white/12 rounded-full px-4 py-2.5 text-sm text-center focus:border-[#FF334B] outline-none mb-3" />
            <button data-testid="share-pw-submit" onClick={submitPw} disabled={checking || !pw}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#FF334B] hover:bg-[#E0223A] text-white font-semibold py-2.5 rounded-full transition-colors disabled:opacity-60">
              {checking ? <Loader2 className="w-4 h-4 spin" /> : null} Ver
            </button>
          </div>
        ) : !data ? (
          <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-[#FF334B] spin" /></div>
        ) : (
          <div className="mfp-card p-6 fade-up" data-testid="share-content">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl bg-[#FF334B]/12 flex items-center justify-center">
                {(() => { const I = TYPE[data.type] || Film; return <I className="w-6 h-6 text-[#FF334B]" />; })()}
              </div>
              <div>
                <div className="font-head text-2xl font-bold">{data.title}</div>
                <div className="mono-label">{data.type === "song" ? "Canción" : data.type === "video" ? "Video" : "Cortometraje"} · Metáfora Play</div>
              </div>
            </div>

            {data.result?.poster_path && data.type === "song" && (
              <img src={publicMediaUrl(data.result.poster_path)} alt={data.title} className="w-full rounded-xl mb-4" />
            )}
            {data.type === "song" && data.result?.audio_path && (
              <audio src={publicMediaUrl(data.result.audio_path)} controls className="w-full" data-testid="share-audio" />
            )}
            {data.type === "video" && data.result?.video_path && (
              <video src={publicMediaUrl(data.result.video_path)} poster={publicMediaUrl(data.result?.poster_path)} controls className="w-full rounded-xl bg-black" data-testid="share-video" />
            )}
            {data.type === "shortfilm" && (() => {
              const clip = data.result?.export_path || (data.scenes || []).find((s) => s.video_path)?.video_path;
              return clip ? <video src={publicMediaUrl(clip)} poster={publicMediaUrl(data.result?.poster_path)} controls className="w-full rounded-xl bg-black" data-testid="share-video" /> : <p className="text-[#64748B]">Sin video disponible.</p>;
            })()}

            {data.result?.lyrics && (
              <pre className="whitespace-pre-wrap text-sm text-[#94A3B8] font-mono bg-[#0B0C13] rounded-xl p-4 border border-white/8 mt-4">{data.result.lyrics}</pre>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
